import React, { useEffect, useState } from "react";
import SEO from "@/components/SEO";
import { Link } from "react-router-dom";
import { Calendar, CheckCircle2, FileText, Users, Mail, ClipboardList, Coins, Image as ImageIcon, ArrowRight, Plus } from "lucide-react";
import { adminApi } from "@/admin/api";
import { StatCard, Panel, EmptyState, StatusPill } from "@/admin/components/ui";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("en-ZA", { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return iso; }
};

export default function AdminDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    adminApi.get("/admin/stats")
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
  }, []);

  const c = data?.counts || {};

  return (
    <div data-testid="page-admin-dashboard" className="space-y-8">
      <SEO title="AdminDashboard · WelkomBiz Photography" noindex />
      {/* Quick actions */}
      <div className="flex flex-wrap gap-2">
        <Link to="/admin/events/new" className="btn-primary !py-2 !px-4 !text-[10px]" data-testid="qa-create-event">
          <Plus className="w-3.5 h-3.5" /> Create Event
        </Link>
        <Link to="/admin/events" className="btn-ghost !py-2 !px-4 !text-[10px]" data-testid="qa-view-events">View Events</Link>
        <Link to="/admin/customers" className="btn-ghost !py-2 !px-4 !text-[10px]" data-testid="qa-view-customers">Customers</Link>
        <Link to="/admin/contacts" className="btn-ghost !py-2 !px-4 !text-[10px]" data-testid="qa-view-contacts">Messages</Link>
        <Link to="/admin/hires" className="btn-ghost !py-2 !px-4 !text-[10px]" data-testid="qa-view-hires">Hire Requests</Link>
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4">
        <StatCard testId="stat-events-total"     label="Total Events" value={c.events_total ?? "—"} icon={FileText} accent="magenta" />
        <StatCard testId="stat-events-published" label="Published" value={c.events_published ?? "—"} icon={CheckCircle2} accent="cyan" />
        <StatCard testId="stat-events-draft"     label="Drafts" value={c.events_draft ?? "—"} icon={FileText} accent="magenta" />
        <StatCard testId="stat-customers"        label="Customers" value={c.customers_total ?? "—"} icon={Users} accent="cyan" />
        <StatCard testId="stat-contacts"         label="Contact Messages" value={c.contacts_total ?? "—"} icon={Mail} accent="magenta" />
        <StatCard testId="stat-hires"            label="Hire Requests" value={c.hires_total ?? "—"} icon={ClipboardList} accent="cyan" />
        <StatCard testId="stat-credits"          label="Credits Assigned" value={c.credits_assigned ?? "—"} icon={Coins} accent="magenta" />
        <StatCard testId="stat-photos"           label="Photos Uploaded" value={c.photos_uploaded ?? "—"} icon={ImageIcon} accent="cyan" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Recent events */}
        <Panel
          testId="panel-recent-events"
          title="Recent Events"
          action={<Link to="/admin/events" className="text-[11px] tracking-luxe uppercase text-white/55 hover:text-white inline-flex items-center gap-1">All <ArrowRight className="w-3 h-3" /></Link>}
        >
          {loading ? <p className="text-sm text-white/40">Loading…</p> :
           (!data?.recent_events || data.recent_events.length === 0)
             ? <EmptyState title="No events yet" message="Create your first event to see it listed here." />
             : (
              <ul className="space-y-3">
                {data.recent_events.map((e) => (
                  <li key={e.id} className="flex items-center justify-between gap-3 py-2 border-b border-white/5 last:border-b-0">
                    <div className="min-w-0">
                      <p className="text-sm text-white truncate">{e.title}</p>
                      <p className="text-[10px] tracking-luxe uppercase text-white/40 mt-0.5">{e.category} · {e.date}</p>
                    </div>
                    <StatusPill status={e.status} />
                  </li>
                ))}
              </ul>
            )}
        </Panel>

        {/* Recent customers */}
        <Panel
          testId="panel-recent-customers"
          title="Recent Customers"
          action={<Link to="/admin/customers" className="text-[11px] tracking-luxe uppercase text-white/55 hover:text-white inline-flex items-center gap-1">All <ArrowRight className="w-3 h-3" /></Link>}
        >
          {loading ? <p className="text-sm text-white/40">Loading…</p> :
           (!data?.recent_customers || data.recent_customers.length === 0)
             ? <EmptyState title="No customers yet" message="Customers will populate here once registration unlocks in Phase 3." />
             : (
              <ul className="space-y-3">
                {data.recent_customers.map((c2) => (
                  <li key={c2.id} className="flex items-center justify-between gap-3 py-2 border-b border-white/5 last:border-b-0">
                    <div className="min-w-0">
                      <p className="text-sm text-white truncate">{c2.name} {c2.surname}</p>
                      <p className="text-[10px] tracking-luxe uppercase text-white/40 mt-0.5">{c2.email}</p>
                    </div>
                    <span className="text-xs text-white/55">{c2.credit_balance ?? 0} cr</span>
                  </li>
                ))}
              </ul>
            )}
        </Panel>

        {/* Recent contacts */}
        <Panel
          testId="panel-recent-contacts"
          title="Recent Contact Messages"
          action={<Link to="/admin/contacts" className="text-[11px] tracking-luxe uppercase text-white/55 hover:text-white inline-flex items-center gap-1">All <ArrowRight className="w-3 h-3" /></Link>}
        >
          {loading ? <p className="text-sm text-white/40">Loading…</p> :
           (!data?.recent_contacts || data.recent_contacts.length === 0)
             ? <EmptyState title="No messages yet" />
             : (
              <ul className="space-y-3">
                {data.recent_contacts.map((m) => (
                  <li key={m.id} className="flex items-center justify-between gap-3 py-2 border-b border-white/5 last:border-b-0">
                    <div className="min-w-0">
                      <p className="text-sm text-white truncate">{m.name}</p>
                      <p className="text-[11px] text-white/55 truncate">{m.email}</p>
                    </div>
                    <span className="text-[10px] tracking-luxe uppercase text-white/40 shrink-0 inline-flex items-center gap-1">
                      <Calendar className="w-3 h-3" /> {fmtDate(m.created_at)}
                    </span>
                  </li>
                ))}
              </ul>
            )}
        </Panel>

        {/* Recent hires */}
        <Panel
          testId="panel-recent-hires"
          title="Recent Hire Requests"
          action={<Link to="/admin/hires" className="text-[11px] tracking-luxe uppercase text-white/55 hover:text-white inline-flex items-center gap-1">All <ArrowRight className="w-3 h-3" /></Link>}
        >
          {loading ? <p className="text-sm text-white/40">Loading…</p> :
           (!data?.recent_hires || data.recent_hires.length === 0)
             ? <EmptyState title="No hire requests yet" />
             : (
              <ul className="space-y-3">
                {data.recent_hires.map((h) => (
                  <li key={h.id} className="flex items-center justify-between gap-3 py-2 border-b border-white/5 last:border-b-0">
                    <div className="min-w-0">
                      <p className="text-sm text-white truncate">{h.name} {h.surname} — {h.event_type}</p>
                      <p className="text-[11px] text-white/55 truncate">{h.email} · {h.service_needed}</p>
                    </div>
                    <span className="text-[10px] tracking-luxe uppercase text-white/40 shrink-0">{fmtDate(h.created_at)}</span>
                  </li>
                ))}
              </ul>
            )}
        </Panel>
      </div>
    </div>
  );
}
